import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import "./CatalogueShare.css";
import logo from "./jojlogo-720.png";
import { catalogueShareApi as api, errorCode } from "./catalogueShareApi";
import { normalizeMobile } from "./phone";
import { GROUPS, STR, errorText } from "./strings";

// thejoj.com/c/{token} — catalogues a Jewels of Joy rep shared with a lead over WhatsApp.
// gate (enter the mobile number the link was sent to) → grid → viewer (page images, lazy loaded).
// The unlock is remembered on this device until the view token expires.

const SALES_MOBILE = "7698888229";
const ENDED = ["ACCESS_ENDED", "LINK_EXPIRED", "LINK_REVOKED"];

const storeKey = (t) => `jojcat:${t}`;
function loadStore(t) {
  try { return JSON.parse(localStorage.getItem(storeKey(t)) || "null"); } catch { return null; }
}
function saveStore(t, v) {
  try { localStorage.setItem(storeKey(t), JSON.stringify(v)); } catch { /* private mode */ }
}
function clearStore(t) {
  try { localStorage.removeItem(storeKey(t)); } catch { /* ignore */ }
}
const browserHindi = () => (navigator.language || "").toLowerCase().startsWith("hi");
const waLink = (mobile, text) =>
  `https://wa.me/91${mobile}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
const initials = (name) =>
  String(name || "J").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const fmtDate = (iso, lang) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "short" });
  } catch { return "—"; }
};

// ── icons (inline, no icon library on this site) ─────────────────────────────
const IconWa = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2z" />
  </svg>
);
const IconCall = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
  </svg>
);
const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
  </svg>
);
const IconDownload = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <path d="M12 3v12m0 0-4-4m4 4 4-4M4 19h16" />
  </svg>
);
const IconBack = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

// ── header ───────────────────────────────────────────────────────────────────
function Header({ lang, setLang }) {
  return (
    <header className="cs-head">
      <div className="cs-head-row">
        <img className="cs-logo" src={logo} alt="Jewels Of Joy" />
        <div className="cs-lang">
          <button type="button" className={lang === "en" ? "on" : ""} onClick={() => setLang("en")}>EN</button>
          <button type="button" className={lang === "hi" ? "on" : ""} onClick={() => setLang("hi")}>हिं</button>
        </div>
      </div>
    </header>
  );
}

function Footer({ lang }) {
  return (
    <footer className="cs-foot">
      <img className="cs-logo dark" src={logo} alt="Jewels Of Joy" /><br />
      Contact: 80000 02808 · Sales: 76988 88229<br />
      <a href="https://thejoj.com/">thejoj.com</a><br />
      <span>{STR[lang].footNote}</span>
    </footer>
  );
}

// ── gate: enter the number ───────────────────────────────────────────────────
function Gate({ token, meta, lang, onUnlocked, onEnded }) {
  const s = STR[lang];
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    const m10 = normalizeMobile(mobile);
    if (!m10) { setError(errorText(lang, "INVALID_NUMBER")); return; }
    setBusy(true);
    setError("");
    try {
      const res = await api.unlock(token, m10);
      onUnlocked(res);
    } catch (err) {
      const code = errorCode(err);
      if (code === "LINK_EXPIRED" || code === "LINK_REVOKED") onEnded(code);
      else setError(errorText(lang, code));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cs-gate">
      <h1>{s.gateTitle}</h1>
      <p>{s.gateSub(meta?.RepName, meta?.CatalogueCount || 0)}</p>
      <form className="cs-gate-card" onSubmit={submit}>
        <label htmlFor="cs-mobile">{s.gateLabel}</label>
        <div className="cs-phone">
          <span>+91</span>
          <input
            id="cs-mobile"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            placeholder={s.gatePlaceholder}
            value={mobile}
            onChange={(e) => { setMobile(e.target.value); setError(""); }}
            maxLength={16}
          />
        </div>
        {error && <div className="cs-err">{error}</div>}
        <button className="cs-primary" type="submit" disabled={busy}>{busy ? s.loading : s.gateBtn}</button>
        <div className="cs-note">{s.gateNote}</div>
      </form>
    </div>
  );
}

// ── viewer: the catalogue's pages ────────────────────────────────────────────
function Viewer({ vt, item, share, lang, onClose, onAuthError }) {
  const s = STR[lang];
  const [dims, setDims] = useState(null);
  const [loaded, setLoaded] = useState(() => new Set([1, 2, 3]));
  const [current, setCurrent] = useState(1);
  const pagesRef = useRef(null);
  const maxPageRef = useRef(1);
  const openedAt = useRef(Date.now());
  const lastSent = useRef({ at: Date.now(), maxPage: 0 });

  useEffect(() => {
    let alive = true;
    api.catalogue(vt, item.CatalogueID)
      .then((d) => alive && setDims(d.Pages || []))
      .catch(() => alive && onAuthError());
    return () => { alive = false; };
  }, [vt, item.CatalogueID, onAuthError]);

  // Android/browser back closes the viewer instead of leaving the page.
  useEffect(() => {
    window.history.pushState({ csViewer: true }, "");
    const onPop = () => onClose();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [onClose]);

  const sendProgress = useCallback(() => {
    const seconds = Math.round((Date.now() - openedAt.current) / 1000);
    if (maxPageRef.current === lastSent.current.maxPage && Date.now() - lastSent.current.at < 20000) return;
    lastSent.current = { at: Date.now(), maxPage: maxPageRef.current };
    api.progress(vt, item.CatalogueID, maxPageRef.current, seconds);
  }, [vt, item.CatalogueID]);

  useEffect(() => {
    const timer = setInterval(sendProgress, 20000);
    const onHide = () => { if (document.visibilityState === "hidden") sendProgress(); };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onHide);
      sendProgress();
    };
  }, [sendProgress]);

  // Load pages near the screen; let go of pages far away (375-page catalogues on small phones).
  useEffect(() => {
    if (!dims || !pagesRef.current) return;
    const root = pagesRef.current;
    const near = new IntersectionObserver((entries) => {
      const add = entries.filter((e) => e.isIntersecting).map((e) => Number(e.target.dataset.page));
      if (add.length) setLoaded((prev) => {
        const next = new Set(prev);
        add.forEach((p) => next.add(p));
        return next;
      });
    }, { root, rootMargin: "200% 0px" });
    const ratios = new Map();
    const visible = new IntersectionObserver((entries) => {
      entries.forEach((e) => ratios.set(Number(e.target.dataset.page), e.intersectionRatio));
      let best = 1, bestR = -1;
      ratios.forEach((r, p) => { if (r > bestR) { best = p; bestR = r; } });
      setCurrent(best);
      if (best > maxPageRef.current) maxPageRef.current = best;
    }, { root, threshold: [0, 0.25, 0.5, 0.75] });
    root.querySelectorAll("[data-page]").forEach((el) => { near.observe(el); visible.observe(el); });
    return () => { near.disconnect(); visible.disconnect(); };
  }, [dims]);

  useEffect(() => {
    setLoaded((prev) => {
      const far = [...prev].filter((p) => Math.abs(p - current) > 15);
      if (!far.length) return prev;
      const next = new Set(prev);
      far.forEach((p) => next.delete(p));
      return next;
    });
  }, [current]);

  const total = dims ? dims.length : item.PageCount;

  return (
    <div className="cs-viewer" role="dialog" aria-label={item.Title}>
      <div className="cs-v-top">
        <button type="button" onClick={() => window.history.back()} aria-label="Back"><IconBack /></button>
        <b>{item.Title}</b>
        {share.AllowDownload && (
          <a href={api.downloadUrl(vt, item.CatalogueID)} aria-label={s.download}><IconDownload size={20} /></a>
        )}
      </div>
      <div className="cs-v-pages" ref={pagesRef}>
        {!dims ? (
          <div className="cs-center"><div className="cs-spinner" /></div>
        ) : (
          dims.map(([w, h], i) => {
            const p = i + 1;
            const show = loaded.has(p);
            return (
              <div
                key={p}
                data-page={p}
                className={`cs-v-page${show ? "" : " loading"}`}
                style={{ aspectRatio: `${w} / ${h}` }}
              >
                {show && (
                  <img
                    src={api.pageUrl(vt, item.CatalogueID, item.VersionID, p)}
                    alt={`${item.Title} ${p}`}
                    draggable={false}
                    onError={onAuthError}
                  />
                )}
                <span className="cs-v-mark">{s.sharedWith(share.Masked)}</span>
              </div>
            );
          })
        )}
      </div>
      <div className="cs-v-foot">
        <span>{s.pageOf(current, total)}</span>
        <span>{share.AllowDownload ? s.pagesLoad : `${s.viewOnly} · ${s.pagesLoad}`}</span>
      </div>
    </div>
  );
}

// ── grid: the catalogue list ─────────────────────────────────────────────────
function Grid({ vt, share, lang, onOpen, onAuthError }) {
  const s = STR[lang];
  const [group, setGroup] = useState("all");
  const [query, setQuery] = useState("");
  const items = useMemo(() => share.Items || [], [share]);
  const repMobile = share.Rep?.Mobile || SALES_MOBILE;

  const inGroup = (it, g) => g === "all" || (g === "new" ? it.IsNew : it.GroupKey === g);
  const chips = GROUPS.map((g) => ({ ...g, n: items.filter((it) => inGroup(it, g.key)).length })).filter((g) => g.n > 0);
  const q = query.trim().toLowerCase();
  const shown = items.filter((it) => inGroup(it, group) && (!q || it.Title.toLowerCase().includes(q)));
  const orderText = lang === "hi"
    ? "नमस्ते, मैंने Jewels of Joy के कैटलॉग देखे। मुझे ऑर्डर करना है:"
    : "Namaste, I saw the Jewels of Joy catalogues. I want to order:";

  return (
    <>
      <section className="cs-hero">
        <h1>{s.hello(share.LeadName)}</h1>
        <p>{s.intro(share.Rep?.Name, items.length)}</p>
        <div className="cs-stats">
          <div className="cs-stat"><b>{items.length}</b><span>{s.sCat}</span></div>
          <div className="cs-stat"><b>₹</b><span>{s.sPrice}</span></div>
          <div className="cs-stat"><b>{fmtDate(share.LastUpdated, lang)}</b><span>{s.sUpd}</span></div>
        </div>
      </section>

      <div className="cs-rep">
        <div className="cs-avatar">{initials(share.Rep?.Name || "Jewels of Joy")}</div>
        <div className="cs-rep-t">
          <b>{share.Rep?.Name || "Jewels of Joy"}</b>
          <span>{s.repRole}</span>
        </div>
        <a className="cs-icon-btn wa" href={waLink(repMobile)} target="_blank" rel="noreferrer" aria-label="WhatsApp"><IconWa /></a>
        <a className="cs-icon-btn call" href={`tel:+91${repMobile}`} aria-label="Call"><IconCall /></a>
      </div>

      <div className="cs-bar">
        <div className="cs-search">
          <IconSearch />
          <input type="search" value={query} placeholder={s.search} onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
        </div>
        {chips.length > 2 && (
          <div className="cs-chips">
            {chips.map((g) => (
              <button key={g.key} type="button" className={`cs-chip${g.key === group ? " on" : ""}`} onClick={() => setGroup(g.key)}>
                {g[lang]}<small>{g.n}</small>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="cs-grid">
        {shown.length === 0 ? (
          <div className="cs-empty">{s.empty}</div>
        ) : shown.map((it) => (
          <article className="cs-card" key={it.CatalogueID}>
            <button type="button" className="cs-ph" onClick={() => onOpen(it)} aria-label={it.Title}>
              <img src={api.coverUrl(vt, it.CatalogueID, it.VersionID)} alt="" loading="lazy" draggable={false} onError={onAuthError} />
              {it.IsNew && <span className="cs-new">{s.newB}</span>}
            </button>
            <div className="cs-bd">
              <h4>{it.Title}</h4>
              <div className="cs-meta">{it.PageCount} {s.pages}{share.AllowDownload ? ` · ${it.SizeMB} MB` : ""}</div>
              {share.AllowDownload && it.SizeMB >= 25 && <div className="cs-warn">{s.large}</div>}
              <div className="cs-acts">
                <button type="button" className="cs-btn-view" onClick={() => onOpen(it)}>{s.view}</button>
                {share.AllowDownload && (
                  <a className="cs-btn-dl" href={api.downloadUrl(vt, it.CatalogueID)} aria-label={s.download}><IconDownload /></a>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      <section className="cs-order">
        <h3>{s.orderH}</h3>
        <p>{s.orderP}</p>
        <a className="cs-wa-btn" href={waLink(repMobile, orderText)} target="_blank" rel="noreferrer">
          <IconWa /> {s.orderBtn}
        </a>
      </section>
    </>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────
export default function CatalogueSharePage() {
  const { token } = useParams();
  const stored = useMemo(() => loadStore(token), [token]);
  const [lang, setLangState] = useState(() => stored?.lang || (browserHindi() ? "hi" : "en"));
  const [phase, setPhase] = useState("loading");     // loading | gate | grid | ended | invalid | error
  const [meta, setMeta] = useState(null);
  const [vt, setVt] = useState(null);
  const [share, setShare] = useState(null);
  const [viewer, setViewer] = useState(null);
  const recheckBusy = useRef(false);
  const langPicked = useRef(!!stored?.lang);

  const setLang = (l) => {
    langPicked.current = true;
    setLangState(l);
    const cur = loadStore(token) || {};
    saveStore(token, { ...cur, lang: l });
  };
  const applyLinkLang = (l) => { if (!langPicked.current && (l === "hi" || l === "en")) setLangState(l); };

  const loadMeta = useCallback(async () => {
    try {
      const m = await api.meta(token);
      setMeta(m);
      applyLinkLang(m.Lang);
      setPhase(m.Status === "ok" ? "gate" : "ended");
    } catch (err) {
      setPhase(errorCode(err) === "LINK_NOT_FOUND" ? "invalid" : "error");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadShare = useCallback(async (theVt) => {
    try {
      const sh = await api.share(theVt);
      const next = sh.Vt || theVt;
      setVt(next);
      setShare(sh);
      applyLinkLang(sh.Lang);
      const cur = loadStore(token) || {};
      saveStore(token, { ...cur, vt: next, exp: Date.parse(sh.VtExpiresOn) || Date.now() + 86400000 });
      setPhase("grid");
      return true;
    } catch (err) {
      const code = errorCode(err);
      if (ENDED.includes(code)) {
        clearStore(token);
        setViewer(null);
        await loadMeta();
        setPhase("ended");
      } else if (code === "LINK_NOT_FOUND") {
        setPhase("invalid");
      } else if (code === "TOKEN_INVALID" || code === "HTTP_401") {
        clearStore(token);
        setVt(null);
        setViewer(null);
        await loadMeta();
      } else {
        setPhase("error");
      }
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, loadMeta]);

  useEffect(() => {
    if (stored?.vt && stored.exp > Date.now()) loadShare(stored.vt);
    else loadMeta();
  }, [stored, loadShare, loadMeta]);

  // An image refused → find out why (access ended, link revoked, token expired) once.
  const onAuthError = useCallback(() => {
    if (recheckBusy.current || !vt) return;
    recheckBusy.current = true;
    loadShare(vt).finally(() => { setTimeout(() => { recheckBusy.current = false; }, 5000); });
  }, [vt, loadShare]);

  const closeViewer = useCallback(() => setViewer(null), []);
  const s = STR[lang];
  const repMobile = (share?.Rep?.Mobile) || meta?.RepMobile || SALES_MOBILE;
  const repName = share?.Rep?.Name || meta?.RepName;

  return (
    <div className="cs-root" lang={lang}>
      <Header lang={lang} setLang={setLang} />

      {phase === "loading" && <div className="cs-center"><div className="cs-spinner" /></div>}

      {phase === "gate" && (
        <Gate
          token={token}
          meta={meta}
          lang={lang}
          onUnlocked={(res) => {
            const cur = loadStore(token) || {};
            saveStore(token, { ...cur, vt: res.Vt, exp: Date.parse(res.ExpiresOn) || Date.now() + 86400000 });
            setPhase("loading");
            loadShare(res.Vt);
          }}
          onEnded={() => setPhase("ended")}
        />
      )}

      {phase === "grid" && share && (
        <Grid vt={vt} share={share} lang={lang} onOpen={setViewer} onAuthError={onAuthError} />
      )}

      {phase === "ended" && (
        <div className="cs-center">
          <div>
            <h2>{s.endedTitle}</h2>
            <p>{s.contactRep(repName)}</p>
            <a className="cs-wa-btn" href={waLink(repMobile)} target="_blank" rel="noreferrer"><IconWa /> {s.chatRep}</a>
          </div>
        </div>
      )}

      {phase === "invalid" && (
        <div className="cs-center">
          <div>
            <h2>{s.invalidTitle}</h2>
            <p>{s.invalidSub}</p>
            <a className="cs-wa-btn" href={waLink(SALES_MOBILE)} target="_blank" rel="noreferrer"><IconWa /> {s.chatRep}</a>
          </div>
        </div>
      )}

      {phase === "error" && (
        <div className="cs-center">
          <div>
            <p>{s.errors.NETWORK}</p>
            <button type="button" className="cs-primary" onClick={() => window.location.reload()}>{s.retry}</button>
          </div>
        </div>
      )}

      {phase !== "loading" && <Footer lang={lang} />}

      {viewer && share && vt && (
        <Viewer vt={vt} item={viewer} share={share} lang={lang} onClose={closeViewer} onAuthError={onAuthError} />
      )}
    </div>
  );
}
